import { Box, Typography } from "@mui/material";
import React from "react";
import { getCommaFormattedNumber } from "../../../../utils/methods/stringMethods";
import { useTranslation } from "react-i18next";

/**
 * PriceIndicator - A component to display price points on a horizontal scale
 *
 * @param {Object} props
 * @param {number|string} props.lowPrice - The low price point value
 * @param {number|string} props.averagePrice - The average price point value
 * @param {number|string} props.awesomePrice - The high/awesome price point value
 * @param {string} props.currency - Currency symbol to show (defaults to "$")
 * @param {string} props.primaryColor - Main color for the active dots and lines
 * @param {string} props.backgroundColor - Background color for inactive parts
 * @param {string} props.textColor - Color for the price text
 * @param {number} props.dotSize - Size of the dots in pixels
 * @param {number} props.lineHeight - Height of connecting lines in pixels
 */
const PriceIndicator = ({
  lowPrice = 0,
  averagePrice = 0,
  awesomePrice = 0,
  currency = "$",
  textColor = "#555555",
  dotSize = 32,
  lineHeight = 4,
}) => {

  const { t } = useTranslation("priceIndicator");

  return (
    <Box sx={{ width: "100%", mt: 4, mb: 4 }}>
      {/* Container for the line and dots */}
      <Box sx={{ position: "relative", height: dotSize, mb: 1 }}>
        {/* Background line */}
        <Box
          sx={{
            position: "absolute",
            top: dotSize / 2 - lineHeight / 2,
            left: dotSize / 2,
            right: dotSize / 2,
            height: lineHeight,
            backgroundColor: "primary.main",
            borderRadius: lineHeight / 2,
          }}
        />

        {/* Dots and labels container */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            position: "relative",
          }}
        >
          {/* Low price dot and label */}
          <Box sx={{ position: "relative", textAlign: "center" }}>
            <Box
              sx={{
                width: dotSize,
                height: dotSize,
                backgroundColor: "primary.main",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            />
            <Typography
              color={"text.highlight"}
              sx={{ mt: 1, fontWeight: "bold" }}
            >
              {t("Low")}:
              <Typography
                variant="span"
                component={"span"}
                sx={{ color: textColor }}
              >
                {currency}
                {getCommaFormattedNumber(lowPrice)}
              </Typography>
            </Typography>
          </Box>

          {/* Average price dot and label */}
          <Box sx={{ position: "relative", textAlign: "center" }}>
            <Box
              sx={{
                width: dotSize,
                height: dotSize,
                backgroundColor: "primary.main",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                ml: 12,
              }}
            />
            <Typography
              color="text.highlight"
              sx={{ mt: 1, fontWeight: "bold", ml: 6 }}
            >
              {t("Average")}:
              <Typography
                variant="span"
                component={"span"}
                sx={{ color: textColor }}
              >
                {currency}
                {getCommaFormattedNumber(averagePrice)}
              </Typography>
            </Typography>
          </Box>

          {/* Awesome price dot and label */}
          <Box sx={{ position: "relative", textAlign: "center" }}>
            <Box
              sx={{
                width: dotSize,
                height: dotSize,
                backgroundColor: "primary.main",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                ml: 12,
              }}
            />
            <Typography
              color="text.highlight"
              sx={{ mt: 1, fontWeight: "bold" }}
            >
              {t("Awesome")}:
              <Typography
                variant="span"
                component={"span"}
                sx={{ color: textColor }}
              >
                {currency}
                {getCommaFormattedNumber(awesomePrice)}
              </Typography>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PriceIndicator;
